import React from 'react';
import { useDispatch } from 'react-redux';
import { useGetUserDataQuery, UserRoleType } from '@src/core/features';
import { trackError, toTrackedError } from '@src/core/sentry';
import { ArcSocketContext, ChannelRequestEnum, ChannelResponseEnum } from '@src/core/socket';
import { setCameraStatus } from '@src/features/order/pages/checklist-page/redux';
import { iceServers } from './ice-servers';
import { CameraSource, CameraViewMode, IGlobalCameraContext } from './types';

const CONNECTION_TIMEOUT = 15000;
const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY = 3000;
const DISCONNECT_DEFER_MS = 300;

export const GlobalCameraContext = React.createContext<IGlobalCameraContext | null>(null);

export const useGlobalCamera = (): IGlobalCameraContext => {
  const ctx = React.useContext(GlobalCameraContext);
  if (!ctx) {
    throw new Error('useGlobalCamera must be used within GlobalCameraProvider');
  }
  return ctx;
};

const generateViewerId = () => 'viewer_' + Math.random().toString(36).substr(2, 9);

const shouldUseSfu = (source: CameraSource, role?: UserRoleType) =>
  Boolean(source.isViewer) ||
  role === UserRoleType.OPERATOR ||
  role === UserRoleType.VIEWER;

const createHiddenHost = () => {
  const el = document.createElement('div');
  el.setAttribute('data-global-camera-host', 'hidden');
  el.style.cssText = 'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;pointer-events:none;overflow:hidden;';
  return el;
};

const createVideoElement = () => {
  const el = document.createElement('video');
  el.autoplay = true;
  el.playsInline = true;
  el.muted = true;
  el.style.width = '100%';
  el.style.height = '100%';
  el.style.objectFit = 'cover';
  el.style.objectPosition = 'center';
  return el;
};

export const GlobalCameraProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch();
  const { data: userData } = useGetUserDataQuery();
  const { arcSocket, subscribe, unsubscribe, readyState } = React.useContext(ArcSocketContext);

  const [activeSource, setActiveSource] = React.useState<CameraSource | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isConnected, setIsConnected] = React.useState(false);
  const [isReconnecting, setIsReconnecting] = React.useState(false);
  const [reconnectAttempts, setReconnectAttempts] = React.useState(0);
  const [viewMode, setViewModeState] = React.useState<CameraViewMode>('default');
  const [collapsed, setCollapsed] = React.useState(false);

  const activeSourceRef = React.useRef<CameraSource | null>(null);
  const isLoadingRef = React.useRef(false);
  const isConnectedRef = React.useRef(false);
  const isReconnectingRef = React.useRef(false);
  const reconnectAttemptsRef = React.useRef(0);
  const viewerIdRef = React.useRef<string>(generateViewerId());
  const roleRef = React.useRef(userData?.role);

  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const peerConnectionRef = React.useRef<RTCPeerConnection | null>(null);
  const hiddenHostRef = React.useRef<HTMLDivElement | null>(null);
  const connectionTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const reconnectTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const disconnectTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  if (videoRef.current === null && typeof document !== 'undefined') {
    videoRef.current = createVideoElement();
  }

  activeSourceRef.current = activeSource;
  roleRef.current = userData?.role;

  const updateIsLoading = React.useCallback((val: boolean) => {
    isLoadingRef.current = val;
    setIsLoading(val);
  }, []);

  const updateIsConnected = React.useCallback((val: boolean) => {
    isConnectedRef.current = val;
    setIsConnected(val);
  }, []);

  const updateIsReconnecting = React.useCallback((val: boolean) => {
    isReconnectingRef.current = val;
    setIsReconnecting(val);
  }, []);

  const updateReconnectAttempts = React.useCallback((next: number | ((prev: number) => number)) => {
    setReconnectAttempts(prev => {
      const value = typeof next === 'function' ? next(prev) : next;
      reconnectAttemptsRef.current = value;
      return value;
    });
  }, []);

  const clearConnectionTimeout = React.useCallback(() => {
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
  }, []);

  const clearReconnectTimeout = React.useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const clearDisconnectTimeout = React.useCallback(() => {
    if (disconnectTimeoutRef.current) {
      clearTimeout(disconnectTimeoutRef.current);
      disconnectTimeoutRef.current = null;
    }
  }, []);

  const updateCameraStatus = React.useCallback((connected: boolean) => {
    const serial = activeSourceRef.current?.serialNumber;
    updateIsConnected(connected);
    updateIsLoading(false);
    if (serial) {
      dispatch(setCameraStatus({ serialNumber: serial, connected }));
    }
  }, [dispatch, updateIsConnected, updateIsLoading]);

  const sendSignal = React.useCallback((command: string, data: unknown) => {
    const source = activeSourceRef.current;
    if (!source) return;

    const webSocket = arcSocket();
    if (!webSocket || webSocket.readyState !== WebSocket.OPEN) return;

    const payload: Record<string, unknown> = {
      type: ChannelRequestEnum.WEBRTC,
      command,
      data,
      serialNumber: source.serialNumber,
    };

    if (shouldUseSfu(source, roleRef.current)) {
      payload.viewerMode = 'sfu';
      payload.viewerId = viewerIdRef.current;
    }

    webSocket.send(JSON.stringify(payload));
  }, [arcSocket]);

  const teardownPeerConnection = React.useCallback(() => {
    const source = activeSourceRef.current;
    const webSocket = arcSocket();

    if (webSocket && webSocket.readyState === WebSocket.OPEN && source) {
      const payload: Record<string, unknown> = {
        type: ChannelRequestEnum.WEBRTC,
        command: 'stop',
        data: null,
        serialNumber: source.serialNumber,
      };
      if (shouldUseSfu(source, roleRef.current)) {
        payload.viewerMode = 'sfu';
        payload.viewerId = viewerIdRef.current;
      }
      webSocket.send(JSON.stringify(payload));
    }

    const video = videoRef.current;
    if (video?.srcObject) {
      const stream = video.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      video.srcObject = null;
    }

    peerConnectionRef.current?.getTransceivers().forEach(t => t.stop());
    peerConnectionRef.current?.close();
    peerConnectionRef.current = null;

    updateIsConnected(false);
    if (source) {
      dispatch(setCameraStatus({ serialNumber: source.serialNumber, connected: false }));
    }
  }, [arcSocket, dispatch, updateIsConnected]);

  const initWebRTC = React.useCallback(async () => {
    try {
      const webSocket = arcSocket();
      if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
        console.warn('WebSocket not ready, will retry when connected');
        updateIsLoading(false);
        return;
      }

      const source = activeSourceRef.current;
      if (!source) {
        updateIsLoading(false);
        return;
      }

      clearConnectionTimeout();
      connectionTimeoutRef.current = setTimeout(() => {
        if (isLoadingRef.current && !isConnectedRef.current) {
          console.warn('Camera connection timeout');
          updateIsLoading(false);
        }
      }, CONNECTION_TIMEOUT);

      const pc = new RTCPeerConnection({
        iceServers,
        iceCandidatePoolSize: 10,
        rtcpMuxPolicy: 'require',
        bundlePolicy: 'max-bundle',
      });
      peerConnectionRef.current = pc;

      const offer = await pc.createOffer({
        offerToReceiveVideo: true,
        offerToReceiveAudio: true,
      });
      await pc.setLocalDescription(offer);

      pc.ontrack = (event: RTCTrackEvent) => {
        if (videoRef.current) {
          videoRef.current.srcObject = event.streams[0];
        }
      };

      const useSfu = shouldUseSfu(source, roleRef.current);
      sendSignal(useSfu ? 'sfu_offer' : 'offer', offer.sdp);

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          sendSignal(
            useSfu ? 'sfu_ice_candidate' : 'ice_candidate',
            JSON.stringify(event.candidate),
          );
        }
      };

      pc.oniceconnectionstatechange = () => {
        const state = peerConnectionRef.current?.iceConnectionState;
        if (state === 'failed') {
          console.error('ICE connection failed. Checking network and TURN/STUN servers.');
          updateCameraStatus(false);
          clearConnectionTimeout();
        } else if (state === 'connected' || state === 'completed') {
          updateCameraStatus(true);
          clearConnectionTimeout();
        } else if (state === 'disconnected' || state === 'closed') {
          updateCameraStatus(false);
        }
      };
    } catch (e) {
      trackError(toTrackedError(e), {
        code: 'FE_0003',
        extra: {
          phase: 'initWebRTC',
          serialNumber: activeSourceRef.current?.serialNumber,
          isViewer: activeSourceRef.current?.isViewer,
          useSfu: activeSourceRef.current
            ? shouldUseSfu(activeSourceRef.current, roleRef.current)
            : false,
        },
      });
      console.error('Error initializing WebRTC:', e);
      updateIsLoading(false);
    }
  }, [arcSocket, clearConnectionTimeout, sendSignal, updateCameraStatus, updateIsLoading]);

  const startVideo = React.useCallback(async () => {
    updateIsLoading(true);
    await initWebRTC();
  }, [initWebRTC, updateIsLoading]);

  const reconnectInternalRef = React.useRef<() => Promise<void>>();

  reconnectInternalRef.current = async () => {
    if (isReconnectingRef.current || reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.error('Max reconnection attempts reached or already reconnecting');
      return;
    }

    updateIsReconnecting(true);
    updateReconnectAttempts(prev => prev + 1);

    try {
      teardownPeerConnection();
      await new Promise(resolve => setTimeout(resolve, RECONNECT_DELAY));
      await initWebRTC();
      updateReconnectAttempts(0);
      updateIsReconnecting(false);
    } catch (error) {
      trackError(toTrackedError(error), {
        code: 'FE_0004',
        extra: {
          phase: 'reconnect',
          serialNumber: activeSourceRef.current?.serialNumber,
          attempt: reconnectAttemptsRef.current,
        },
      });
      console.error('Reconnection attempt failed:', error);
      updateIsReconnecting(false);

      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectTimeoutRef.current = setTimeout(
          () => reconnectInternalRef.current?.(),
          RECONNECT_DELAY,
        );
      }
    }
  };

  const reconnect = React.useCallback(() => {
    reconnectInternalRef.current?.();
  }, []);

  const connectCamera = React.useCallback((source: CameraSource) => {
    clearDisconnectTimeout();

    const current = activeSourceRef.current;

    // Same drone: update metadata without tearing down the stream.
    if (current && current.serialNumber === source.serialNumber) {
      const metaChanged =
        Boolean(current.isViewer) !== Boolean(source.isViewer) ||
        Boolean(current.isEnRoute) !== Boolean(source.isEnRoute) ||
        current.resetGoHere !== source.resetGoHere;
      if (metaChanged) {
        setActiveSource(source);
      }
      return;
    }

    // Different or first drone: let the lifecycle effect handle teardown/start.
    updateReconnectAttempts(0);
    updateIsReconnecting(false);
    setActiveSource(source);
  }, [clearDisconnectTimeout, updateIsReconnecting, updateReconnectAttempts]);

  const disconnectCamera = React.useCallback((immediate = false) => {
    const doDisconnect = () => {
      disconnectTimeoutRef.current = null;
      clearConnectionTimeout();
      clearReconnectTimeout();
      teardownPeerConnection();
      setActiveSource(null);
      updateReconnectAttempts(0);
      updateIsReconnecting(false);
    };

    clearDisconnectTimeout();

    if (immediate) {
      doDisconnect();
      return;
    }

    disconnectTimeoutRef.current = setTimeout(doDisconnect, DISCONNECT_DEFER_MS);
  }, [
    clearConnectionTimeout,
    clearDisconnectTimeout,
    clearReconnectTimeout,
    teardownPeerConnection,
    updateIsReconnecting,
    updateReconnectAttempts,
  ]);

  const setViewMode = React.useCallback((mode: CameraViewMode) => {
    setViewModeState(prev => (prev === mode ? 'default' : mode));
  }, []);

  const toggleCollapse = React.useCallback(() => {
    setCollapsed(prev => !prev);
  }, []);

  const mountCountRef = React.useRef(0);

  const mountVideoInto = React.useCallback((target: HTMLElement) => {
    mountCountRef.current += 1;
    // A consumer is now displaying the camera — abort any pending teardown.
    clearDisconnectTimeout();

    const video = videoRef.current;
    if (!video || !target) return;
    if (video.parentElement === target) return;
    target.appendChild(video);

    // Some browsers pause a <video> element when reparented via appendChild.
    // Kick it off again so a live MediaStream resumes painting immediately.
    if (video.srcObject) {
      video.play().catch(() => { /* play() is best-effort */ });
    }
  }, [clearDisconnectTimeout]);

  const unmountVideoFromTarget = React.useCallback((target: HTMLElement) => {
    mountCountRef.current = Math.max(0, mountCountRef.current - 1);

    const video = videoRef.current;
    const hidden = hiddenHostRef.current;
    if (video && hidden && video.parentElement === target) {
      hidden.appendChild(video);
    }

    // If nothing is displaying the camera any longer, schedule a deferred
    // teardown. A new consumer mounting within the defer window cancels it.
    if (mountCountRef.current === 0 && activeSourceRef.current) {
      clearDisconnectTimeout();
      disconnectTimeoutRef.current = setTimeout(() => {
        disconnectTimeoutRef.current = null;
        if (mountCountRef.current > 0) return;
        clearConnectionTimeout();
        clearReconnectTimeout();
        teardownPeerConnection();
        setActiveSource(null);
        updateReconnectAttempts(0);
        updateIsReconnecting(false);
      }, DISCONNECT_DEFER_MS);
    }
  }, [
    clearConnectionTimeout,
    clearDisconnectTimeout,
    clearReconnectTimeout,
    teardownPeerConnection,
    updateIsReconnecting,
    updateReconnectAttempts,
  ]);

  // Create the hidden host and park the <video> there on mount.
  React.useLayoutEffect(() => {
    if (typeof document === 'undefined') return;

    const hidden = createHiddenHost();
    document.body.appendChild(hidden);
    hiddenHostRef.current = hidden;

    const video = videoRef.current;
    if (video && !video.parentElement) {
      hidden.appendChild(video);
    }

    const handleWaiting = () => updateIsLoading(true);
    const handlePlaying = () => updateCameraStatus(true);
    const handleCanPlay = () => updateCameraStatus(true);
    const handleError = () => {
      updateCameraStatus(false);
      reconnectInternalRef.current?.();
    };

    video?.addEventListener('waiting', handleWaiting);
    video?.addEventListener('playing', handlePlaying);
    video?.addEventListener('canplay', handleCanPlay);
    video?.addEventListener('error', handleError);

    return () => {
      video?.removeEventListener('waiting', handleWaiting);
      video?.removeEventListener('playing', handlePlaying);
      video?.removeEventListener('canplay', handleCanPlay);
      video?.removeEventListener('error', handleError);
      hidden.remove();
      hiddenHostRef.current = null;
    };
  }, [updateCameraStatus, updateIsLoading]);

  // React to WebRTC signaling events from the shared socket.
  React.useEffect(() => {
    const handleWEBRTC = (event: { command: string; data: string }) => {
      const source = activeSourceRef.current;
      if (!source) return;

      const useSfu = shouldUseSfu(source, roleRef.current);
      const answerCommand = useSfu
        ? ChannelResponseEnum.WEBRTC_SFU_ANSWER
        : ChannelResponseEnum.WEBRTC_ANSWER;
      const iceCommand = useSfu
        ? ChannelResponseEnum.WEBRTC_SFU_ICE_CANDIDATE
        : ChannelResponseEnum.WEBRTC_ICE_CANDIDATE;

      if (event.command === answerCommand) {
        const pc = peerConnectionRef.current;
        if (pc) {
          pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: event.data }))
            .catch((err) => {
              trackError(toTrackedError(err), {
                code: 'FE_0003',
                extra: { phase: 'setRemoteDescription', serialNumber: source.serialNumber },
              });
              console.error('Error setting remote description:', err);
            });
        }
      } else if (event.command === iceCommand) {
        let candidateObj: RTCIceCandidateInit;
        try {
          candidateObj = JSON.parse(event.data);
          if (candidateObj.candidate) {
            candidateObj.candidate = candidateObj.candidate.trim();
          }
        } catch {
          candidateObj = {
            candidate: event.data,
            sdpMid: '0',
            sdpMLineIndex: 0,
          };
        }
        peerConnectionRef.current
          ?.addIceCandidate(new RTCIceCandidate(candidateObj))
          .catch((err) => {
            trackError(toTrackedError(err), {
              code: 'FE_0005',
              extra: { phase: 'addIceCandidate', serialNumber: source.serialNumber },
            });
            console.error('Error adding ICE candidate:', err);
          });
      }
    };

    subscribe(ChannelResponseEnum.WEBRTC, handleWEBRTC);
    return () => unsubscribe(ChannelResponseEnum.WEBRTC);
  }, [subscribe, unsubscribe]);

  // (Re)start WebRTC when active source changes or socket becomes ready.
  const serialKey = activeSource?.serialNumber ?? null;
  React.useEffect(() => {
    if (!serialKey) return;
    if (!readyState) return;

    teardownPeerConnection();
    startVideo();

    return () => {
      teardownPeerConnection();
      clearReconnectTimeout();
      clearConnectionTimeout();
    };
  }, [serialKey, readyState]);

  // Cleanup on provider unmount (logout / app close).
  React.useEffect(() => {
    return () => {
      clearDisconnectTimeout();
      clearReconnectTimeout();
      clearConnectionTimeout();
      teardownPeerConnection();
    };
  }, []);

  const value = React.useMemo<IGlobalCameraContext>(() => ({
    activeSource,
    isLoading,
    isConnected,
    isReconnecting,
    reconnectAttempts,
    viewMode,
    collapsed,
    videoRef,
    connectCamera,
    disconnectCamera,
    reconnect,
    setViewMode,
    toggleCollapse,
    mountVideoInto,
    unmountVideoFromTarget,
  }), [
    activeSource,
    isLoading,
    isConnected,
    isReconnecting,
    reconnectAttempts,
    viewMode,
    collapsed,
    connectCamera,
    disconnectCamera,
    reconnect,
    setViewMode,
    toggleCollapse,
    mountVideoInto,
    unmountVideoFromTarget,
  ]);

  return (
    <GlobalCameraContext.Provider value={value}>
      {children}
    </GlobalCameraContext.Provider>
  );
};

export default GlobalCameraProvider;
export type { CameraSource, CameraViewMode } from './types';
